import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Camera, Upload, Loader2, X } from "lucide-react";
import { Button } from "@/components/uiDir/button";
import { Input } from "@/components/uiDir/input";
import { Textarea } from "@/components/uiDir/textarea";
import { Label } from "@/components/uiDir/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/uiDir/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/uiDir/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/uiDir/select";
import { Switch } from "@/components/uiDir/switch";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";


const SOCIAL_PLATFORMS = [
  { label: "Facebook", value: "facebook", placeholder: "https://facebook.com/yourprofile" },
  { label: "Instagram", value: "instagram", placeholder: "https://instagram.com/yourprofile" },
  { label: "X / Twitter", value: "twitter", placeholder: "https://x.com/yourprofile" },
  { label: "LinkedIn", value: "linkedin", placeholder: "https://linkedin.com/in/yourprofile" },
  { label: "Behance", value: "behance", placeholder: "https://behance.net/yourprofile" },
  { label: "Dribbble", value: "dribbble", placeholder: "https://dribbble.com/yourprofile" },
  { label: "Pinterest", value: "pinterest", placeholder: "https://pinterest.com/yourprofile" },
  { label: "GitHub", value: "github", placeholder: "https://github.com/yourprofile" },
  { label: "YouTube", value: "youtube", placeholder: "https://youtube.com/@yourchannel" },
  { label: "TikTok", value: "tiktok", placeholder: "https://tiktok.com/@yourprofile" },
  { label: "Portfolio", value: "portfolio", placeholder: "https://yourportfolio.com" },
  { label: "Medium", value: "medium", placeholder: "https://medium.com/@yourprofile" },
  { label: "ArtStation", value: "artstation", placeholder: "https://artstation.com/yourprofile" },
];


export default function EditProfile() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [socialLinks, setSocialLinks] = useState<{ platform: string; url: string; is_visible: boolean }[]>([]);

  
  const [profileData, setProfileData] = useState({
    fullName: "",
    username: "",
    program: "COMSCI",
    studentId: "",
    title: "",
    bio: "",
    about: "",
    location: "",
    phone: "",
    availableForWork: false,
    avatarUrl: "",
    resumeUrl: "",
  });

  useEffect(() => {
    loadProfileData();
  }, []);

  const loadProfileData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/login');
        return;
      }

      setUserId(user.id);

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;

      const { data: links, error: linksError } = await supabase
        .from("social_links")
        .select("platform, url, is_visible")
        .eq("user_id", user.id)
        .order("display_order", { ascending: true });

      if (!linksError && links) {
        setSocialLinks(links);
      }


      if (profile) {
        setProfileData({
          fullName: profile.full_name || "",
          username: profile.username || "",
          program: profile.program || "COMSCI",
          studentId: profile.student_id || "",
          title: profile.title || "",
          bio: profile.bio || "",
          about: profile.about || "",
          location: profile.location || "",
          phone: profile.phone || "",
          availableForWork: profile.available_for_work || false,
          avatarUrl: profile.avatar_url || "",
          resumeUrl: profile.resume_url || "",
        });
        setAvatarPreview(profile.avatar_url);
      }

      setIsLoadingData(false);
    } catch (err) {
      console.error('Error loading profile:', err);
      toast.error('Failed to load profile');
      setIsLoadingData(false);
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleResumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Resume must be less than 5MB');
        return;
      }
      if (file.type !== 'application/pdf') {
        toast.error('Resume must be a PDF file');
        return;
      }
      setResumeFile(file);
      toast.success('Resume selected');
    }
  };

  const uploadAvatar = async () => {
    if (!avatarFile || !userId) return profileData.avatarUrl;

    const fileExt = avatarFile.name.split('.').pop();
    const fileName = `${userId}/avatar.${fileExt}`;

    try {
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, avatarFile, { upsert: true });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      return data.publicUrl;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error('Failed to upload avatar');
      return profileData.avatarUrl;
    }
  };

  const uploadResume = async () => {
    if (!resumeFile || !userId) return profileData.resumeUrl;

    const fileName = `${userId}/resume.pdf`;

    try {
      const { error: uploadError } = await supabase.storage
        .from('resumes')
        .upload(fileName, resumeFile, { upsert: true });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('resumes')
        .getPublicUrl(fileName);

      return data.publicUrl;
    } catch (error) {
      console.error('Error uploading resume:', error);
      toast.error('Failed to upload resume');
      return profileData.resumeUrl;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!userId) return;

    setIsLoading(true);

      for (const link of socialLinks) {
        if (!link.url) continue;

        const { error: linkError } = await supabase
          .from("social_links")
          .upsert({
            user_id: userId,
            platform: link.platform,
            url: link.url,
            is_visible: link.is_visible,
          }, { onConflict: "user_id,platform" });

        if (linkError) console.error(linkError);
      }


    try {
      // Upload files if changed
      const avatarUrl = await uploadAvatar();
      const resumeUrl = await uploadResume();

      // Update profile
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: profileData.fullName,
          username: profileData.username,
          program: profileData.program,
          student_id: profileData.studentId,
          title: profileData.title,
          bio: profileData.bio,
          about: profileData.about,
          location: profileData.location,
          phone: profileData.phone,
          available_for_work: profileData.availableForWork,
          avatar_url: avatarUrl,
          resume_url: resumeUrl,
        })
        .eq('id', userId);

      if (error) throw error;

      toast.success("Profile updated successfully!");
      navigate('/dashboard');
    } catch (err) {
      console.error('Error updating profile:', err);
      toast.error(err.message || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingData) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-10 max-w-4xl">
        <div className="mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
          </Link>
          <h1 className="text-4xl font-heading font-bold mb-2">Edit Profile</h1>
          <p className="text-muted-foreground">Update your personal information and portfolio details</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Profile Photo</CardTitle>
              <CardDescription>Upload a professional photo (max 5MB)</CardDescription>
            </CardHeader>
            <CardContent className="flex items-center gap-6">
              <Avatar className="h-24 w-24">
                <AvatarImage src={avatarPreview || profileData.avatarUrl || `https://api.dicebear.com/7.x/avataaars/svg?seed=${profileData.username}`} />
                <AvatarFallback>{profileData.fullName[0] || 'U'}</AvatarFallback>
              </Avatar>
              <div className="space-y-2">
                <input
                  type="file"
                  id="avatar-upload"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden"
                />
                <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('avatar-upload')?.click()}>
                  <Camera className="mr-2 h-4 w-4" />
                  Change Photo
                </Button>
                <p className="text-xs text-muted-foreground">JPG, PNG or WebP (max 5MB)</p>
              </div>
            </CardContent>
          </Card>

          
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name *</Label>
                  <Input
                    id="fullName"
                    value={profileData.fullName}
                    onChange={(e) => setProfileData({ ...profileData, fullName: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="username">Username *</Label>
                  <Input
                    id="username"
                    value={profileData.username}
                    onChange={(e) => setProfileData({ ...profileData, username: e.target.value.toLowerCase() })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="studentId">Student ID</Label>
                  <Input
                    id="studentId"
                    value={profileData.studentId}
                    onChange={(e) => setProfileData({ ...profileData, studentId: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="program">Program *</Label>
                  <Select value={profileData.program} onValueChange={(value) => setProfileData({ ...profileData, program: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="COMSCI">Computer Science</SelectItem>
                      <SelectItem value="BMMA">Multimedia Arts</SelectItem>
                      <SelectItem value="FILM">Film & Visual Media</SelectItem>
                      <SelectItem value="INFOSYSTEM">Information Systems</SelectItem>
                      <SelectItem value="ENTREPRENEURSHIP">Entrepreneurship</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Professional Title</Label>
                <Input
                  id="title"
                  placeholder="e.g., Full-Stack Developer, UX Designer"
                  value={profileData.title}
                  onChange={(e) => setProfileData({ ...profileData, title: e.target.value })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Available for Work</Label>
                  <p className="text-sm text-muted-foreground">Show a badge on your profile</p>
                </div>
                <Switch
                  checked={profileData.availableForWork}
                  onCheckedChange={(checked) => setProfileData({ ...profileData, availableForWork: checked })}
                />
              </div>
            </CardContent>
          </Card>

          
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>About Me</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bio">Bio (Short)</Label>
                <Textarea
                  id="bio"
                  placeholder="A brief description for your profile card..."
                  value={profileData.bio}
                  onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                  rows={3}
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground text-right">
                  {profileData.bio.length}/500 characters
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="about">About (Detailed)</Label>
                <Textarea
                  id="about"
                  placeholder="Tell us more about yourself, your skills, and experience..."
                  value={profileData.about}
                  onChange={(e) => setProfileData({ ...profileData, about: e.target.value })}
                  rows={5}
                  maxLength={2000}
                />
                <p className="text-xs text-muted-foreground text-right">
                  {profileData.about.length}/2000 characters
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    placeholder="City, Country"
                    value={profileData.location}
                    onChange={(e) => setProfileData({ ...profileData, location: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    placeholder="+63 XXX XXX XXXX"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Social Links</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Add your social or professional links. Only selected platforms will be shown on your profile.
              </p>

              
              <div className="flex items-center gap-3">
                <Select
                  onValueChange={(value) => {
                    const alreadyExists = socialLinks.some((s) => s.platform === value);
                    if (!alreadyExists) {
                      setSocialLinks([
                        ...socialLinks,
                        { platform: value, url: "", is_visible: true },
                      ]);
                    } else {
                      toast.warning("This social platform is already added.");
                    }
                  }}
                >
                  <SelectTrigger className="w-60">
                    <SelectValue placeholder="Add Social Platform" />
                  </SelectTrigger>
                  <SelectContent>
                    {SOCIAL_PLATFORMS.filter(
                      (p) => !socialLinks.find((s) => s.platform === p.value)
                    ).map((platform) => (
                      <SelectItem key={platform.value} value={platform.value}>
                        {platform.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              
              <div className="space-y-4">
                {socialLinks.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No social links added yet.
                  </p>
                ) : (
                  socialLinks.map((link) => {
                    const platform = SOCIAL_PLATFORMS.find(
                      (p) => p.value === link.platform
                    );
                    if (!platform) return null;

                    return (
                      <div
                        key={link.platform}
                        className="flex items-center gap-3 border border-border rounded-lg p-3"
                      >
                        <div className="w-32 font-medium text-sm">{platform.label}</div>
                        <Input
                          placeholder={platform.placeholder}
                          value={link.url}
                          onChange={(e) =>
                            setSocialLinks(
                              socialLinks.map((l) =>
                                l.platform === link.platform
                                  ? { ...l, url: e.target.value }
                                  : l
                              )
                            )
                          }
                        />
                        <Switch
                          checked={link.is_visible}
                          onCheckedChange={(checked) =>
                            setSocialLinks(
                              socialLinks.map((l) =>
                                l.platform === link.platform
                                  ? { ...l, is_visible: checked }
                                  : l
                              )
                            )
                          }
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            setSocialLinks(
                              socialLinks.filter((l) => l.platform !== link.platform)
                            )
                          }
                        >
                          <X className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>


          
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Resume</CardTitle>
              <CardDescription>Upload your latest resume (PDF only, max 5MB)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <input
                type="file"
                id="resume-upload"
                accept="application/pdf"
                onChange={handleResumeChange}
                className="hidden"
              />
              <Button 
                type="button" 
                variant="outline" 
                className="w-full"
                onClick={() => document.getElementById('resume-upload')?.click()}
              >
                <Upload className="mr-2 h-4 w-4" />
                {resumeFile ? 'Change Resume' : 'Upload Resume'}
              </Button>
              {(resumeFile || profileData.resumeUrl) && (
                <p className="text-xs text-muted-foreground">
                  {resumeFile ? `Selected: ${resumeFile.name}` : 'Current resume uploaded'}
                </p>
              )}
            </CardContent>
          </Card>

          
          <div className="flex justify-end gap-4">
            <Link to="/dashboard">
              <Button type="button" variant="outline" disabled={isLoading}>Cancel</Button>
            </Link>
            <Button type="submit" disabled={isLoading} className="bg-gradient-primary">
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

